import React from 'react';
import {
  HeartIcon,
  UserGroupIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

const snpccTables = [
  {
    label: 'Universe Table 1',
    short: 'SNPE',
    description: 'Special Needs Plan Effectiveness - Health Risk Assessments',
    universeType: 'snpcc_snpe',
    gradient: 'from-emerald-500 to-teal-500',
    icon: HeartIcon,
    color: 'emerald'
  },
  {
    label: 'Table 1IA',
    short: 'CC-IA',
    description: 'Care Coordination Impact Analysis - ICP, ICT, transitions of care, and MOC training (columns A-W)',
    universeType: 'snpcc_ccia',
    gradient: 'from-sky-500 to-blue-500',
    icon: UserGroupIcon,
    color: 'sky'
  },
  {
    label: 'Table 2IA (Generated)',
    short: 'HRAT-IA',
    description: 'Health Risk Assessment Timeliness - Impact Analysis',
    universeType: 'snpcc_hrat_ia',
    gradient: 'from-orange-500 to-amber-500',
    icon: DocumentTextIcon,
    color: 'blue',
    isGenerated: true
  }
];

export default function SNPCCTableSelector({ onSelect }) {
  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold bg-gradient-to-r from-slate-900 via-emerald-800 to-teal-800 bg-clip-text text-transparent mb-3">
          SNPCC Universe Tables
        </h3>
        <p className="text-slate-600 text-base max-w-2xl mx-auto leading-relaxed">
          Select the SNPCC universe table for validation. SNPE validates enrollee HRA data, while HRAT-IA is automatically generated for untimely assessments.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {snpccTables.map((table) => (
          <button
            key={table.universeType}
            onClick={() => onSelect(table)}
            disabled={table.isGenerated}
            className={`
              group relative w-full text-left p-6 rounded-2xl border transition-all duration-300
              ${table.isGenerated 
                ? 'bg-slate-50/50 border-slate-200/50 cursor-not-allowed opacity-60'
                : 'bg-white/90 border-slate-200/60 hover:border-emerald-300/60 hover:shadow-lg hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-emerald-500/50'
              }
            `}
          >
            <div className="flex items-start gap-4">
              <div className={`
                w-12 h-12 rounded-xl flex items-center justify-center
                ${table.isGenerated 
                  ? 'bg-slate-200/50'
                  : `bg-gradient-to-r ${table.gradient} shadow-lg group-hover:scale-110 transition-transform duration-300`
                }
              `}>
                <table.icon className={`w-6 h-6 ${table.isGenerated ? 'text-slate-400' : 'text-white'}`} />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className={`font-bold text-lg ${table.isGenerated ? 'text-slate-500' : 'text-slate-900 group-hover:text-emerald-800'} transition-colors`}>
                    {table.label}
                  </h4>
                  <span className={`
                    px-2 py-1 text-xs font-bold rounded-full
                    ${table.isGenerated 
                      ? 'bg-slate-200/50 text-slate-500'
                      : `bg-gradient-to-r ${table.gradient} text-white shadow-sm`
                    }
                  `}>
                    {table.short}
                  </span>
                  {table.isGenerated && (
                    <span className="px-2 py-1 text-xs font-semibold bg-orange-100 text-orange-700 rounded-full">
                      Auto-Generated
                    </span>
                  )}
                </div>
                
                <p className={`text-sm leading-relaxed ${table.isGenerated ? 'text-slate-400' : 'text-slate-600'}`}>
                  {table.description}
                </p>
                
                {table.isGenerated && (
                  <p className="text-xs text-slate-500 mt-2 italic">
                    This table is automatically generated based on SNPE validation results
                  </p>
                )}
              </div>
            </div>

            {!table.isGenerated && (
              <div className="absolute inset-0 rounded-2xl ring-1 ring-transparent group-hover:ring-emerald-300/50 pointer-events-none transition-all duration-300" />
            )}
          </button>
        ))}
      </div>

      <div className="bg-emerald-50/50 border border-emerald-200/60 rounded-xl p-4 mt-6">
        <div className="flex items-start gap-3">
          <UserGroupIcon className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-emerald-800">
            <p className="font-semibold mb-1">SNPCC Validation Process:</p>
            <p>Upload your SNPE universe file with engagement letter date to validate HRA timeliness; HRAT-IA (Table 2IA) is generated automatically for enrollees requiring impact analysis. When CMS requests a Care Coordination Impact Analysis, upload your completed Table 1IA (CC-IA) for full layout and dependency validation before submission.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
